import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { 
  Terminal, Cpu, Check, HelpCircle, AlertTriangle, Plus, Trash2, Crosshair, Orbit, Database, Zap, Layers, Eye, LayoutGrid, Calendar, ArrowUpDown, ShieldCheck, CheckCircle2, ChevronDown, ChevronUp, Search, Info
} from 'lucide-react';

interface TaskNode {
  id: string;
  title: string;
  description: string;
  date: string;
  priority: 'high' | 'medium' | 'low';
  tag: string;
  completed: boolean;
  expanded?: boolean;
  position: THREE.Vector3;
  mesh?: THREE.Mesh;
}

export function NexoraX3DTaskMatrix() {
  const mountRef = useRef<HTMLDivElement>(null);
  
  // Dashboard view selection: '3d' space orbit or 'grid' holographic card board
  const [viewMode, setViewMode] = useState<'3d' | 'grid'>('grid');
  
  // Core Task State
  const [tasks, setTasks] = useState<TaskNode[]>([]);
  
  // Form Inputs
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDesc, setTaskDesc] = useState('');
  const [taskDate, setTaskDate] = useState(() => {
    return new Date().toISOString().slice(0, 10);
  });
  const [taskPriority, setTaskPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const [taskTag, setTaskTag] = useState('');
  
  // Interactive HUD Tracking
  const [selectedTask, setSelectedTask] = useState<TaskNode | null>(null);
  const [detailedTaskId, setDetailedTaskId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'completed'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'priority' | 'title'>('date');
  const [pulseTaskId, setPulseTaskId] = useState<string | null>(null);

  // Real-time Spatial Telemetry Compiler States
  const [spatialResult, setSpatialResult] = useState<any | null>(null);
  const [isCompilingSpatial, setIsCompilingSpatial] = useState(false);

  useEffect(() => {
    setSpatialResult(null);
  }, [detailedTaskId]);

  const compileSpatialTelemetry = async (task: TaskNode) => {
    setIsCompilingSpatial(true);
    setSpatialResult(null);
    try {
      const response = await fetch('/api/category27/spatial-telemetry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task_id: task.id,
          title: task.title,
          coordinate_axis: {
            x: parseFloat(task.position.x.toFixed(4)),
            y: parseFloat(task.position.y.toFixed(4)),
            z: parseFloat(task.position.z.toFixed(4)),
          }
        }),
      });
      const resData = await response.json();
      if (resData.status === 'success') {
        setSpatialResult(resData.data);
      } else {
        setSpatialResult({ error: resData.message || 'Compiler failed.' });
      }
    } catch (err: any) {
      setSpatialResult({ error: err.message || 'NexoraX hardware connection timed out.' });
    } finally {
      setIsCompilingSpatial(false);
    }
  };

  // ThreeJS Space References
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const meshGroupRef = useRef<THREE.Group | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);
  const starsRef = useRef<THREE.Points | null>(null);

  // 1. Initialize Seed Tasks
  useEffect(() => {
    const seedTasks: TaskNode[] = [
      {
        id: 'TASK-902',
        title: 'AI Infrastructure Stabilization',
        description: 'Proprietary AI network orchestration to optimize 0.8s SLA endpoints under direct mandate of Director Lead Director.',
        date: '2026-06-12',
        priority: 'high',
        tag: 'Node-09.2',
        completed: false,
        expanded: false,
        position: new THREE.Vector3(-3.2, 1.6, -1.5)
      },
      {
        id: 'TASK-315',
        title: 'Resource Financial Ledger Sync',
        description: 'Integrate £150,000 Resource resource with official Secure Payment Provider corporate account (Verified Payment Configuration // Secure Billing Provider) with ledger checksum logic.',
        date: '2026-06-15',
        priority: 'medium',
        tag: 'Node-07',
        completed: false,
        expanded: false,
        position: new THREE.Vector3(2.8, 2.2, 1.2)
      },
      {
        id: 'TASK-487',
        title: 'NexoraX Taxation Daemon',
        description: 'Synchronize live 1% 3% Community Allocation router split equally across Charities 1, 2, 3 and deduce 10% Maintenance & Hosting Feees.',
        date: '2026-06-10',
        priority: 'low',
        tag: 'Chronos-17',
        completed: true,
        expanded: false,
        position: new THREE.Vector3(0.5, 0.9, -2.8)
      }
    ];
    setTasks(seedTasks);
  }, []);

  // 2. Setup ThreeJS Environment with proper sizing and clean memory release
  useEffect(() => {
    if (viewMode !== '3d' || !mountRef.current) return;

    // Create Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050a18); // Deep Space dark black-blue matching mockup
    sceneRef.current = scene;

    // Atmospheric Fog
    scene.fog = new THREE.FogExp2(0x050a18, 0.05);

    // Create Camera
    const width = mountRef.current.clientWidth || 800;
    const height = mountRef.current.clientHeight || 500;
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.set(0, 7, 13);
    cameraRef.current = camera;

    // Create WebGL Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    
    mountRef.current.innerHTML = '';
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // OrbitControls for easy navigation
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxDistance = 35;
    controls.minDistance = 3;
    controlsRef.current = controls;

    // Underlying digital grid cyber system (requested in prompt: underlying cyber grid matrix lines system)
    const gridHelper = new THREE.GridHelper(30, 30, 0x00d4ff, 0x1e293b);
    gridHelper.position.y = 0;
    if (gridHelper.material instanceof THREE.Material) {
      gridHelper.material.transparent = true;
      gridHelper.material.opacity = 0.16;
    }
    scene.add(gridHelper);

    // Dual subgrid elements for high tech depth structure
    const subGridHelper = new THREE.GridHelper(30, 60, 0xff3b5c, 0x0f172a);
    subGridHelper.position.y = -0.05;
    if (subGridHelper.material instanceof THREE.Material) {
      subGridHelper.material.transparent = true;
      subGridHelper.material.opacity = 0.06;
    }
    scene.add(subGridHelper);

    // Dynamic Multi-NexoraX Lighting system
    const ambientLight = new THREE.AmbientLight(0x1e293b, 1.5);
    scene.add(ambientLight);

    const cyanLight = new THREE.DirectionalLight(0x00d4ff, 1.5);
    cyanLight.position.set(6, 12, 6);
    scene.add(cyanLight);

    const redLight = new THREE.DirectionalLight(0xff3b5c, 1.0);
    redLight.position.set(-6, 8, -6);
    scene.add(redLight);

    const glowCore = new THREE.PointLight(0x00d4ff, 1.2, 18);
    glowCore.position.set(0, 3, 0);
    scene.add(glowCore);

    // Create high tech star particles
    const starsGeo = new THREE.BufferGeometry();
    const starsCount = 350;
    const starCoords = new Float32Array(starsCount * 3);
    for (let i = 0; i < starsCount * 3; i++) {
      starCoords[i] = (Math.random() - 0.5) * 45;
    }
    starsGeo.setAttribute('position', new THREE.BufferAttribute(starCoords, 3));
    const starsMat = new THREE.PointsMaterial({
      color: 0x4f8eff,
      size: 0.14,
      transparent: true,
      opacity: 0.5,
      sizeAttenuation: true
    });
    const starField = new THREE.Points(starsGeo, starsMat);
    scene.add(starField);
    starsRef.current = starField;

    // Group for containing dynamic task nodes
    const meshGroup = new THREE.Group();
    scene.add(meshGroup);
    meshGroupRef.current = meshGroup;

    // Dynamic Resize observer setup
    const handleResize = () => {
      if (!mountRef.current || !rendererRef.current || !cameraRef.current) return;
      const w = mountRef.current.clientWidth;
      const h = mountRef.current.clientHeight;
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(w, h);
    };

    const resizeObserver = new ResizeObserver(() => handleResize());
    resizeObserver.observe(mountRef.current);

    // Main animation loop (holding sin-hover wave state)
    const animate = () => {
      animationFrameIdRef.current = requestAnimationFrame(animate);

      if (controlsRef.current) controlsRef.current.update();

      const time = performance.now() * 0.0016;
      meshGroup.children.forEach((mesh, index) => {
        mesh.position.y += Math.sin(time + index) * 0.004;
        mesh.rotation.y += 0.007;
        mesh.rotation.x += 0.002;
      });

      if (starsRef.current) {
        starsRef.current.rotation.y = time * 0.012;
      }

      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();

    return () => {
      resizeObserver.disconnect();
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      starsGeo.dispose();
      starsMat.dispose();
      
      meshGroup.children.forEach((mesh) => {
        if (mesh instanceof THREE.Mesh) {
          mesh.geometry.dispose();
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach(m => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });
      scene.clear();
    };
  }, [viewMode]);

  // 3. Maintain state mapping of node arrays to active 3D meshes
  useEffect(() => {
    if (viewMode !== '3d') return;
    const scene = sceneRef.current;
    const meshGroup = meshGroupRef.current;
    if (!scene || !meshGroup) return;

    // Keep memory clean, dispose old child nodes
    while (meshGroup.children.length > 0) {
      const child = meshGroup.children[0];
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else {
          child.material.dispose();
        }
      }
      meshGroup.remove(child);
    }

    // Map tasks to ThreeJS blocks
    tasks.forEach((task) => {
      // Glow boundaries priority colors based on user specs: Low (green), Medium (amber), High (red)
      let colorValue = 0xffb800; // Medium: Amber glow (#ffb800)
      if (task.priority === 'high') colorValue = 0xff3b5c; // High: Red glow (#ff3b5c)
      if (task.priority === 'low') colorValue = 0x00ff88;  // Low: Green glow (#00ff88)

      // Box design mesh
      const geo = new THREE.BoxGeometry(1.5, 0.45, 0.9);
      const mat = new THREE.MeshPhongMaterial({
        color: colorValue,
        emissive: colorValue,
        emissiveIntensity: selectedTask?.id === task.id ? 0.35 : 0.15,
        transparent: true,
        opacity: task.completed ? 0.35 : selectedTask?.id === task.id ? 0.95 : 0.75,
        flatShading: true,
        shininess: 95
      });

      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(task.position);
      
      // Wireframe overlay tracking
      const wireGeo = new THREE.BoxGeometry(1.6, 0.52, 0.98);
      const wireMat = new THREE.MeshBasicMaterial({
        color: colorValue,
        wireframe: true,
        transparent: true,
        opacity: selectedTask?.id === task.id ? 0.8 : 0.2
      });
      const wireMesh = new THREE.Mesh(wireGeo, wireMat);
      mesh.add(wireMesh);

      mesh.userData = { taskId: task.id };
      task.mesh = mesh;
      meshGroup.add(mesh);
    });
  }, [tasks, selectedTask, viewMode]);

  // 4. Handle Injection of new Task Node
  const createNew3DTaskNode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskTitle.trim() || !taskDesc.trim()) return;

    // Vector randomized starting coordinate mapping
    const randomPos = new THREE.Vector3(
      (Math.random() - 0.5) * 8, 
      Math.random() * 2.2 + 0.8, 
      (Math.random() - 0.5) * 8  
    );

    const newId = `TASK-${Math.floor(100 + Math.random() * 900)}`;

    const newTask: TaskNode = {
      id: newId,
      title: taskTitle.trim(),
      description: taskDesc.trim(),
      date: taskDate || new Date().toISOString().slice(0, 10),
      priority: taskPriority,
      tag: taskTag.trim() || 'Node-Telemetry',
      completed: false,
      expanded: false,
      position: randomPos
    };

    setTasks(prev => [newTask, ...prev]);
    
    // Auto trigger single pulse entry animation
    setPulseTaskId(newId);
    setTimeout(() => {
      setPulseTaskId(null);
    }, 450);

    // Focus immediately on new entry
    setSelectedTask(newTask);

    // Clean inputs
    setTaskTitle('');
    setTaskDesc('');
    setTaskTag('');
  };

  // Toggle node completion with trigger animation logic
  const toggleTaskCompletion = (id: string) => {
    setTasks(prev => prev.map(task => {
      if (task.id === id) {
        if (!task.completed) {
          // Trigger the beautiful pulse class
          setPulseTaskId(id);
          setTimeout(() => setPulseTaskId(null), 450);
        }
        return { ...task, completed: !task.completed };
      }
      return task;
    }));
  };

  // Toggle card expand logic for detail viewing
  const toggleTaskExpanded = (id: string) => {
    setTasks(prev => prev.map(task => {
      if (task.id === id) {
        return { ...task, expanded: !task.expanded };
      }
      return task;
    }));
  };

  // Remove element safely
  const removeTaskNode = (id: string) => {
    setTasks(prev => prev.filter(t => t.id !== id));
    if (selectedTask?.id === id) {
      setSelectedTask(null);
    }
  };

  // fly camera to task mesh
  const focusAndFlyToTaskMesh = (task: TaskNode) => {
    setSelectedTask(task);
    if (!cameraRef.current || !controlsRef.current) return;

    const targetPos = task.position.clone();
    const startCam = cameraRef.current.position.clone();
    const endCam = targetPos.clone().add(new THREE.Vector3(0, 2, 4.5));
    const startLook = controlsRef.current.target.clone();
    const endLook = targetPos.clone();

    let progress = 0;
    const animateFly = () => {
      progress += 0.055;
      if (progress <= 1.0) {
        cameraRef.current?.position.lerpVectors(startCam, endCam, progress);
        controlsRef.current?.target.lerpVectors(startLook, endLook, progress);
        requestAnimationFrame(animateFly);
      } else {
        cameraRef.current?.position.copy(endCam);
        controlsRef.current!.target.copy(endLook);
      }
    };
    animateFly();
  };

  // Filter & Sort Tasks logic
  const processedTasks = tasks
    .filter(task => {
      // Search Box filter
      const searchStr = searchQuery.toLowerCase();
      const matchSearch = task.title.toLowerCase().includes(searchStr) || 
                          task.description.toLowerCase().includes(searchStr) || 
                          task.tag.toLowerCase().includes(searchStr);
      
      // Priority filter
      const matchPriority = filterPriority === 'all' || task.priority === filterPriority;
      
      // Status filter
      const matchStatus = filterStatus === 'all' || 
                          (filterStatus === 'completed' && task.completed) || 
                          (filterStatus === 'active' && !task.completed);
                          
      return matchSearch && matchPriority && matchStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
      if (sortBy === 'priority') {
        const priorityWeight = { high: 3, medium: 2, low: 1 };
        return priorityWeight[b.priority] - priorityWeight[a.priority];
      }
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });

  return (
    <section className="w-full max-w-7xl mx-auto bg-[#050a18]/95 border border-slate-900 rounded-3xl relative overflow-hidden backdrop-blur-2xl shadow-[0_25px_60px_rgba(0,0,0,0.85)] font-mono text-left " id="nexorax-task-matrix-container">
      
      {/* Cyber ambient glows in background */}
      <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-purple-500/5 rounded-full blur-[90px] pointer-events-none translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[450px] h-[450px] bg-cyan-500/5 rounded-full blur-[90px] pointer-events-none -translate-x-1/3 translate-y-1/3"></div>

      {/* Mandatory Arabic/English Religious Praise Header */}
      <div className="bg-slate-950/95 border-b border-cyan-500/20 p-4 flex flex-col items-center justify-center text-center gap-2 relative z-20">
        <p className="text-xs sm:text-sm text-cyan-400 font-extrabold tracking-wide leading-relaxed">
          "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
        </p>
        <span className="text-[9px] text-[#8ab4f8] uppercase tracking-widest font-black flex items-center gap-2">
          <Orbit className="w-3.5 h-3.5 animate-spin-slow text-[#00d4ff]" />
          NEXORAX NEXORAX TELEMETRY NODES • Nexorax Task Matrix
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 relative z-10 min-h-[680px]">
        
        {/* SIDEBAR: NX-CONTROL-SIDEBAR STYLE (Glass-Morphic control deck panel) */}
        <aside className="lg:col-span-4 bg-slate-950/45 border-r border-slate-900/85 p-6 flex flex-col gap-5 backdrop-blur-md">
          
          <div>
            <h2 className="text-xl font-black text-cyan-400 uppercase tracking-widest flex items-center gap-2 drop-shadow-[0_0_8px_rgba(0,212,255,0.4)]">
              <Terminal className="w-5 h-5 animate-pulse text-[#00d4ff]" />
              Nexorax Task
            </h2>
            <div className="text-[9px] text-[#4f8eff] tracking-[2px] uppercase font-black pl-0.5 mt-1">NexoraX OS Module</div>
          </div>

          <form onSubmit={createNew3DTaskNode} className="space-y-4 pt-2">
            
            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Task Title</label>
              <input
                type="text"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                placeholder="Directive signature ID..."
                className="w-full bg-[#020617]/80 border border-slate-800/80 focus:border-cyan-500/70 rounded-lg p-3 text-xs text-white placeholder:text-slate-600 focus:outline-none transition-all font-mono"
                required
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Detail Description</label>
              <textarea
                value={taskDesc}
                onChange={(e) => setTaskDesc(e.target.value)}
                placeholder="Detail parameter sequences..."
                className="w-full bg-[#020617]/80 border border-slate-800/80 focus:border-cyan-500/70 rounded-lg p-3 text-xs text-white placeholder:text-slate-600 focus:outline-none transition-all min-h-[75px] resize-none font-mono"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Due Date</label>
                <input
                  type="date"
                  value={taskDate}
                  onChange={(e) => setTaskDate(e.target.value)}
                  className="w-full bg-[#020617]/80 border border-slate-800/80 focus:border-cyan-500/70 rounded-lg p-2.5 text-[11px] text-zinc-300 focus:outline-none focus:ring-0 cursor-pointer font-mono"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Category / Tag</label>
                <input
                  type="text"
                  value={taskTag}
                  onChange={(e) => setTaskTag(e.target.value)}
                  placeholder="e.g. Node-27"
                  className="w-full bg-[#020617]/80 border border-slate-800/80 focus:border-cyan-500/70 rounded-lg p-2.5 text-xs text-white placeholder:text-slate-600 focus:outline-none transition-all font-mono"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Priority Track</label>
              <div className="grid grid-cols-3 gap-2">
                {(['low', 'medium', 'high'] as const).map((p) => {
                  const colorMap = {
                    high: 'border-rose-500/40 text-rose-400 bg-rose-950/20 hover:text-rose-300 focus:ring-0',
                    medium: 'border-yellow-500/40 text-yellow-400 bg-yellow-950/20 hover:text-yellow-300 focus:ring-0',
                    low: 'border-emerald-500/40 text-emerald-400 bg-emerald-950/20 hover:text-emerald-300 focus:ring-0'
                  };
                  const activeColorMap = {
                    high: 'border-rose-500 text-rose-400 bg-rose-950/50 shadow-[0_0_12px_rgba(255,59,92,0.3)]',
                    medium: 'border-yellow-500 text-yellow-500 bg-yellow-950/50 shadow-[0_0_12px_rgba(255,184,0,0.3)]',
                    low: 'border-emerald-500 text-emerald-400 bg-emerald-950/50 shadow-[0_0_12px_rgba(0,255,136,0.3)]'
                  };
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setTaskPriority(p)}
                      className={`py-2 px-1 text-[9px] font-black uppercase tracking-widest rounded-lg border cursor-pointer transition-all ${
                        taskPriority === p ? activeColorMap[p] : `bg-black/30 border-slate-800 text-slate-500 hover:bg-slate-900/40`
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-[#00d4ff] hover:opacity-95 text-black font-black uppercase text-[10px] py-3 tracking-widest rounded-lg transition-all duration-200 mt-2 hover:shadow-[0_0_20px_rgba(0,212,255,0.45)] cursor-pointer flex items-center justify-center gap-2 border-0"
            >
              <Plus className="w-3.5 h-3.5 stroke-[3px]" />
              Inject Task Element (1p)
            </button>
          </form>

          {/* Quick HUD Diagnostics display inside the panel */}
          <div className="mt-auto border-t border-slate-900/70 pt-4 text-left">
            <span className="text-[8px] text-slate-500 uppercase tracking-widest font-extrabold block mb-2">TELEMETRY STREAM STATS</span>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-[#02050c]/80 border border-slate-900 rounded p-2">
                <div className="text-xs text-white font-extrabold">{tasks.length}</div>
                <div className="text-[7px] text-slate-500 uppercase">TOTAL</div>
              </div>
              <div className="bg-[#02050c]/80 border border-slate-900 rounded p-2">
                <div className="text-xs text-emerald-400 font-extrabold">{tasks.filter(t => t.completed).length}</div>
                <div className="text-[7px] text-emerald-500 uppercase">SOLVED</div>
              </div>
              <div className="bg-[#02050c]/80 border border-slate-900 rounded p-2">
                <div className="text-xs text-rose-400 font-extrabold">{tasks.filter(t => !t.completed).length}</div>
                <div className="text-[7px] text-rose-500 uppercase">ACTIVE</div>
              </div>
            </div>
          </div>
        </aside>

        {/* WORKSPACE CANVAS / GRID ZONE */}
        <main className="lg:col-span-8 p-6 flex flex-col gap-6 bg-slate-950/20">
          
          {/* CONTROL SORTING BAR (Glass Card with filters + view switches) */}
          <div className="bg-[#0f172a]/55 backdrop-blur-md border border-cyan-500/20 px-5 py-3.5 rounded-xl flex flex-col sm:flex-row justify-between items-center gap-4 shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
            
            {/* Left: View mode toggler */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3.5 py-1.5 rounded-lg border text-[9px] uppercase font-black tracking-wider flex items-center gap-1.5 transition-all cursor-pointer ${
                  viewMode === 'grid' 
                    ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400' 
                    : 'bg-black/30 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <LayoutGrid className="w-3 h-3" />
                Holographic Grid
              </button>
              <button
                onClick={() => setViewMode('3d')}
                className={`px-3.5 py-1.5 rounded-lg border text-[9px] uppercase font-black tracking-wider flex items-center gap-1.5 transition-all cursor-pointer ${
                  viewMode === '3d' 
                    ? 'bg-purple-500/20 border-purple-500 text-purple-400' 
                    : 'bg-black/30 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <Orbit className="w-3 h-3 text-cyan-400 animate-spin-slow" />
                3D Spatial Orbit
              </button>
            </div>

            {/* Right: Searches & Filters */}
            <div className="flex flex-wrap items-center justify-end gap-2.5 w-full sm:w-auto">
              {/* Search Element */}
              <div className="relative flex-1 sm:flex-initial">
                <Search className="w-3 h-3 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter key..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-black/30 border border-slate-800 rounded px-2.5 py-1 text-[10px] text-zinc-300 pl-7 outline-none focus:border-cyan-500/50 w-full sm:w-32 placeholder:text-slate-600"
                />
              </div>

              {/* Priority Selection Filter */}
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value as any)}
                className="bg-black/30 border border-slate-800 rounded px-2 py-1 text-[10px] text-zinc-300 outline-none focus:border-cyan-500/50"
              >
                <option value="all">All Priorities</option>
                <option value="high">High Level</option>
                <option value="medium">Medium Level</option>
                <option value="low">Low Level</option>
              </select>

              {/* Sort logic selector */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="bg-black/30 border border-slate-800 rounded px-2 py-1 text-[10px] text-zinc-300 outline-none focus:border-cyan-500/50"
              >
                <option value="date">Sort: Due Date</option>
                <option value="priority">Sort: Priority</option>
                <option value="title">Sort: Signature</option>
              </select>
            </div>

          </div>

          {/* VIEW: THREEJS SPATIAL PERSPECTIVE */}
          {viewMode === '3d' && (
            <div className="flex-1 flex flex-col gap-4">
              
              {/* Main ThreeJS rendering viewport */}
              <div className="relative bg-black/95 border border-slate-900 rounded-2xl overflow-hidden shadow-[inset_0_0_40px_rgba(0,0,0,0.9)] flex-1 min-h-[440px] flex flex-col">
                
                {/* Visual grid coordinates badge top-left */}
                <div className="absolute top-4 left-4 z-20 pointer-events-none flex flex-col gap-1 text-[10px]">
                  <span className="bg-slate-950/90 border border-slate-800 px-3 py-1 text-slate-300 tracking-wider rounded uppercase font-bold flex items-center gap-1.5 shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
                    <Orbit className="w-3.5 h-3.5 text-cyan-400 animate-spin-slow" />
                    CHRONOS_LEAD_COORDINATES: ACTIVE
                  </span>
                  <span className="text-[8px] text-slate-500 uppercase tracking-widest font-black inline-block pl-1 pt-1">
                    PORT 3000 RENDERING • THREE.JS WEBGL CONTEXT
                  </span>
                </div>

                {/* Camera navigation tips bottom-left */}
                <div className="absolute bottom-4 left-4 z-20 pointer-events-none bg-slate-950/85 border border-slate-800/80 px-3 py-2 rounded text-[8px] text-slate-400 uppercase tracking-widest font-bold max-w-xs space-y-1">
                  <span className="text-[9px] text-[#00d4ff] block pb-0.5 border-b border-slate-800/50">SPATIAL CONTROL CHEATS</span>
                  <p>• Left Click + Drag: Orbit Camera</p>
                  <p>• Wheel Scroll: Coordinate Zoom</p>
                  <p>• Right Click + Drag: Pan Coordinates</p>
                </div>

                {/* Mounting Container of canvas */}
                <div 
                  ref={mountRef} 
                  className="w-full h-full absolute inset-0 z-10 cursor-grab active:cursor-grabbing"
                  id="canvas-container"
                ></div>
              </div>

              {/* Connected Active elements list for easy targeting */}
              <div className="bg-[#050b18]/60 border border-slate-900 rounded-2xl p-4">
                <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-widest mb-2.5 flex items-center gap-2 pb-1.5 border-b border-slate-900">
                  <Database className="w-3.5 h-3.5 text-cyan-400" />
                  CLICK TO POSITION AND ORBIT TARGET COORDINATES
                </span>
                <div className="flex flex-wrap gap-2 max-h-[110px] overflow-y-auto pr-1">
                  {processedTasks.map(t => (
                    <button
                      key={t.id}
                      onClick={() => focusAndFlyToTaskMesh(t)}
                      className={`px-3 py-1.5 rounded-lg border text-[9px] uppercase font-black tracking-wider transition-all flex items-center gap-1.5 cursor-pointer ${
                        selectedTask?.id === t.id 
                          ? 'bg-cyan-500/10 border-cyan-500 text-cyan-400 shadow-[0_0_10px_rgba(0,212,255,0.15)]' 
                          : 'bg-black/40 border-slate-800 text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      <Crosshair className="w-3 h-3" />
                      {t.id} - {t.title}
                    </button>
                  ))}
                  {processedTasks.length === 0 && (
                    <span className="text-[9px] text-slate-500 py-2 uppercase">No matching active nodes found</span>
                  )}
                </div>
              </div>

              {/* Real-time Telemetry detail card */}
              <AnimatePresence mode="wait">
                {selectedTask && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="bg-[#030611] border border-cyan-500/25 p-4 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative overflow-hidden"
                  >
                    <div className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
                    <div className="space-y-1 text-left max-w-[75%]">
                      <div className="flex items-center gap-2">
                        <span className={`text-[8px] font-black px-1.5 py-0.5 uppercase tracking-widest rounded text-black font-mono ${
                          selectedTask.priority === 'high' ? 'bg-[#ff3b5c]' : selectedTask.priority === 'medium' ? 'bg-[#ffb800]' : 'bg-[#00ff88]'
                        }`}>
                          {selectedTask.priority}
                        </span>
                        <span className="text-zinc-500 text-[10px] font-bold">[{selectedTask.id}] • {selectedTask.tag}</span>
                      </div>
                      <h4 className="text-sm font-bold text-white uppercase">{selectedTask.title}</h4>
                      <p className="text-[10px] text-slate-400 font-mono tracking-wide leading-relaxed">{selectedTask.description}</p>
                    </div>
                    <div className="flex gap-2 w-full md:w-auto">
                      <button
                        onClick={() => toggleTaskCompletion(selectedTask.id)}
                        className={`text-[9px] font-black px-3 py-1.5 rounded uppercase tracking-wider border cursor-pointer ${
                          selectedTask.completed 
                            ? 'bg-emerald-950/20 border-emerald-500 text-emerald-400' 
                            : 'bg-black/40 border-slate-800 text-slate-400 hover:text-slate-100'
                        }`}
                      >
                        {selectedTask.completed ? 'Solved' : 'Solve Task'}
                      </button>
                      <button
                        onClick={() => setSelectedTask(null)}
                        className="text-[9px] font-black px-3 py-1.5 bg-black/50 border border-slate-900 rounded text-slate-500 hover:text-slate-300 uppercase tracking-wider cursor-pointer"
                      >
                        Close
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

            </div>
          )}

          {/* VIEW: HOLOGRAPHIC TASK CARDS DECK (User specified CSS & HTML styles) */}
          {viewMode === 'grid' && (
            <div className="flex-1 flex flex-col gap-6">

              {/* Grid block container */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" id="holographic-task-grid">
                
                {processedTasks.map((task) => {
                  const isPulse = pulseTaskId === task.id;
                  const glowClass = task.priority === 'high' 
                    ? 'border-l-4 border-l-[#ff3b5c] hover:shadow-[0_10px_25px_rgba(255,59,92,0.15)] focus:border-cyan-500/50' 
                    : task.priority === 'medium' 
                    ? 'border-l-4 border-l-[#ffb800] hover:shadow-[0_10px_25px_rgba(255,184,0,0.15)] focus:border-cyan-500/50' 
                    : 'border-l-4 border-l-[#00ff88] hover:shadow-[0_10px_25px_rgba(0,255,136,0.15)] focus:border-cyan-500/50';

                  return (
                    <motion.div
                      key={task.id}
                      layoutId={`task-card-hero-${task.id}`}
                      onClick={() => setDetailedTaskId(task.id)}
                      className={`bg-slate-950/55 backdrop-blur-md border border-slate-900 rounded-xl p-5 flex flex-col justify-between gap-4 transition-all duration-300 relative cursor-pointer shadow-[0_4px_15px_rgba(0,0,0,0.2)] hover:border-cyan-500/60 hover:-translate-y-1 block text-left mt-3.5 ${glowClass} ${
                        task.completed ? 'opacity-40 border-l-[#64748b] border-slate-800' : ''
                      } ${isPulse ? 'ring-1 ring-[#00ff88] scale-[1.02] duration-150' : ''}`}
                    >
                      {/* Category tab indicator at the top */}
                      <div className="absolute -top-2.5 left-4 bg-[#050a18] border border-slate-800 border-b-0 text-[7px] text-[#00d4ff] px-2.5 py-0.5 rounded-t-md font-black uppercase tracking-widest font-mono flex items-center gap-1 shadow-[0_-4px_10px_rgba(0,0,0,0.5)] z-10 transition-all group-hover:border-cyan-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00d4ff] inline-block animate-pulse"></span>
                        Category: {task.tag}
                      </div>

                      {/* Decorative inner corner overlay */}
                      <div className="absolute top-1 right-1 w-[6px] h-[6px] border-t border-r border-slate-800 rounded-tr pointer-events-none"></div>
                      
                      <div className="space-y-2">
                        {/* Top Metadata Row */}
                        <div className="flex justify-between items-start gap-2 border-b border-slate-900 pb-2">
                          <div className="space-y-0.5">
                            <span className="text-[7.5px] text-zinc-500 font-bold block uppercase tracking-widest">SIGNATURE</span>
                            <span className={`text-[9.5px] font-black uppercase ${
                              task.completed ? 'text-slate-500 line-through' : 'text-white'
                            }`}>
                              {task.title}
                            </span>
                          </div>
                          <span className="text-[7.5px] font-extrabold uppercase bg-slate-900 border border-slate-800 px-2 py-0.5 rounded text-[#4f8eff] font-mono tracking-widest">
                            {task.tag}
                          </span>
                        </div>

                        {/* Description field - expand option matching user specs */}
                        <p className={`text-[10px] text-slate-400 font-mono tracking-wide leading-relaxed line-clamp-2`}>
                          {task.description}
                        </p>

                        <div className="flex items-center gap-1.5 text-[8px] text-cyan-400 uppercase font-black pt-1">
                          <Eye className="w-3 h-3 text-cyan-400 animate-pulse" />
                          Click card to view details & system logs
                        </div>
                      </div>

                      {/* Bottom Controls Deck */}
                      <div className="flex justify-between items-center border-t border-slate-950 pt-3 mt-1.5 text-[9px]">
                        <span className="text-[8px] text-slate-500 uppercase tracking-widest font-black flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-slate-600" />
                          {task.date}
                        </span>

                        <div className="flex gap-1.5 z-25" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => toggleTaskCompletion(task.id)}
                            className={`px-2 py-1 text-[8px] font-black uppercase rounded border transition-all duration-200 cursor-pointer ${
                              task.completed 
                                ? 'bg-[#00ff88]/20 border-[#00ff88] text-[#00ff88]' 
                                : 'bg-black/60 border-slate-800 text-emerald-400 hover:bg-[#00ff88] hover:text-[#050a18]'
                            }`}
                            title="Coordinate solution protocol"
                          >
                            {task.completed ? 'Solved' : 'Solve'}
                          </button>
                          <button
                            onClick={() => removeTaskNode(task.id)}
                            className="px-2 py-1 text-[8px] font-black uppercase rounded border bg-black/60 border-slate-800 text-rose-400 hover:bg-rose-500 hover:text-[#050a18] transition-all duration-200 cursor-pointer"
                            title="Liquidate node"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                    </motion.div>
                  );
                })}

                {processedTasks.length === 0 && (
                  <div className="col-span-full bg-slate-950/20 border border-dashed border-slate-900 rounded-xl py-14 flex flex-col justify-center items-center text-center text-slate-500 font-extrabold uppercase tracking-widest gap-2">
                    <Info className="w-5 h-5 text-slate-600 animate-pulse" />
                    <span>-- No active telemetry node matches details --</span>
                  </div>
                )}

              </div>

            </div>
          )}

          {/* HOLOGRAPHIC DETAIL MODAL OVERLAY */}
          <AnimatePresence>
            {detailedTaskId && (() => {
              const detailedTask = tasks.find(t => t.id === detailedTaskId);
              if (!detailedTask) return null;
              
              const glowClass = detailedTask.priority === 'high' 
                ? 'border-l-4 border-l-[#ff3b5c] shadow-[0_15px_40px_rgba(255,59,92,0.25)]' 
                : detailedTask.priority === 'medium' 
                ? 'border-l-4 border-l-[#ffb800] shadow-[0_15px_40px_rgba(255,184,0,0.25)]' 
                : 'border-l-4 border-l-[#00ff88] shadow-[0_15px_40px_rgba(0,255,136,0.25)]';

              const simulatedLogs = [
                { time: "00:01:04", info: "INIT_DAEMON_PULL", status: "SUCCESS", detail: "Loaded core 0.8s SLA latency constraints." },
                { time: "00:01:06", info: "SECURE_HANDSHAKE", status: "ONLINE", detail: `Establishing NexoraX channel to ${detailedTask.tag}.` },
                { time: "00:01:09", info: "PORTAL_MAPPING", status: "ACK", detail: "Routing to Secure Payment Provider (Verified Payment Configuration // Secure Billing Provider) active." },
                { time: "00:01:12", info: "LEDGER_VALUATION", status: "SECURE", detail: `Synchronizing Resource resource database ledger.` },
                { time: "00:01:15", info: "NEXORAX_RULES", status: "ENFORCED", detail: "1% 3% Community Allocation router checked." },
                { time: "00:01:18", info: "BACKUP_DAEMON", status: "SYNCED", detail: "CloudSync-02 NexoraX Resource Library mirror nominal." },
                { time: "00:01:21", info: "MEMBER_VERIFY", status: "VIP_PASS", detail: "Director Lead Director credentials verified." },
                { time: "00:01:24", info: "NODE_STATUS", status: detailedTask.completed ? "RESOLVED" : "PENDING_HANDSHAKE", detail: detailedTask.completed ? "System back to nominal capacity." : "Awaiting operational resolution protocol." }
              ];

              return (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
                  <div className="absolute inset-0" onClick={() => setDetailedTaskId(null)} />
                  
                  <motion.div
                    layoutId={`task-card-hero-${detailedTask.id}`}
                    className={`bg-[#050a18] border border-slate-800 rounded-3xl p-6 md:p-8 flex flex-col gap-6 w-full max-w-2xl relative shadow-[0_20px_50px_rgba(0,0,0,0.8)] text-left ${glowClass}`}
                  >
                    {/* Visual Category Badge at Top Left */}
                    <div className="absolute -top-3 left-6 bg-[#050a18] border border-slate-800 border-b-0 text-[8px] text-[#00d4ff] px-4 py-1 rounded-t-lg font-black uppercase tracking-widest font-mono flex items-center gap-1.5 shadow-[0_-4px_10px_rgba(0,0,0,0.5)] z-10">
                      <span className="w-2 h-2 rounded-full bg-[#00d4ff] inline-block animate-pulse"></span>
                      Category: {detailedTask.tag}
                    </div>

                    {/* Decorative subtle corner widgets */}
                    <div className="absolute top-2 right-2 w-[10px] h-[10px] border-t border-r border-slate-800 rounded-tr pointer-events-none"></div>
                    
                    {/* Header Information */}
                    <div className="flex justify-between items-start gap-4 border-b border-slate-900 pb-4">
                      <div className="space-y-1 text-left">
                        <span className="text-[9px] text-[#4f8eff] font-bold block uppercase tracking-widest">TACTICAL TELEMETRY NODE</span>
                        <h2 className={`text-lg md:text-xl font-extrabold uppercase tracking-tight text-white ${detailedTask.completed ? 'line-through opacity-70' : ''}`}>
                          {detailedTask.title}
                        </h2>
                        <p className="text-[10px] text-slate-500 font-mono">NODE IDENTIFICATION: <span className="text-slate-300 font-bold">{detailedTask.id}</span></p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <span className={`text-[9px] font-black px-2.5 py-1 uppercase tracking-widest rounded text-black font-mono ${
                          detailedTask.priority === 'high' ? 'bg-[#ff3b5c]' : detailedTask.priority === 'medium' ? 'bg-[#ffb800]' : 'bg-[#00ff88]'
                        }`}>
                          {detailedTask.priority} PRIORITY
                        </span>
                        <span className="text-[9px] text-slate-500 font-mono uppercase font-semibold">
                          DUE: {detailedTask.date}
                        </span>
                      </div>
                    </div>

                    {/* Full Description Segment */}
                    <div className="space-y-2">
                      <span className="text-[8.5px] text-zinc-500 font-bold block uppercase tracking-widest">CORE COMMAND BRIEFING</span>
                      <p className="text-[12.1px] text-slate-300 font-mono tracking-wide leading-relaxed bg-slate-950/40 p-4 rounded-xl border border-slate-900/60 text-left">
                        {detailedTask.description}
                      </p>
                    </div>

                    {/* SPATIAL TELEMETRY INTEGRATION segment */}
                    <div className="space-y-2.5 bg-slate-950/30 border border-slate-900/80 p-4 rounded-xl">
                      <div className="flex justify-between items-center gap-4 flex-wrap sm:flex-nowrap">
                        <div className="flex flex-col text-left">
                          <span className="text-[8.5px] text-zinc-500 font-bold uppercase tracking-widest">SPATIAL CHANNELS & VECTOR QUANTUM ROUTER</span>
                          <span className="text-[10px] text-[#00ff88]/95 font-mono">
                            Coordinates: X:{detailedTask.position.x.toFixed(2)}, Y:{detailedTask.position.y.toFixed(2)}, Z:{detailedTask.position.z.toFixed(2)}
                          </span>
                        </div>
                        <button
                          onClick={() => compileSpatialTelemetry(detailedTask)}
                          disabled={isCompilingSpatial}
                          className="px-3.5 py-1.5 bg-[#00ff88] text-slate-950 font-black text-[9px] uppercase tracking-wider rounded-md hover:bg-emerald-400 active:scale-95 disabled:opacity-50 transition-all cursor-pointer flex items-center gap-1.5"
                        >
                          {isCompilingSpatial ? (
                            <>
                              <Cpu className="w-3 h-3 animate-spin" />
                              COMPILING...
                            </>
                          ) : (
                            <>
                              <Zap className="w-3 h-3" />
                              COMPILE FIELD VECTORS
                            </>
                          )}
                        </button>
                      </div>

                      {spatialResult && (
                        <div className="bg-slate-950/80 border border-slate-900/65 rounded-lg p-3 space-y-2.5 font-mono text-[9px] text-zinc-400">
                          {spatialResult.error ? (
                            <div className="text-rose-400 font-semibold uppercase flex items-center gap-1">
                              <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                              {spatialResult.error}
                            </div>
                          ) : (
                            <div className="space-y-2 text-left">
                              <div className="flex justify-between items-center border-b border-slate-900/50 pb-1 text-[#00ff88]">
                                <span className="font-bold flex items-center gap-1">
                                  <ShieldCheck className="w-3 h-3" />
                                  COMPILER ACKNOWLEDGED (Category 27)
                                </span>
                                <span className="text-[8px] bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">STATUS: {spatialResult.status || 'OK'}</span>
                              </div>

                              <p className="text-[8.5px] text-slate-200 tracking-wide font-medium">
                                "{spatialResult.NexoraX_metrics?.announcement || 'NexoraX ledger compiled.'}"
                              </p>

                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 bg-black/40 p-2.5 rounded border border-slate-900 text-[8.5px] text-slate-300 font-mono">
                                <div>
                                  <span className="text-slate-500 block text-[7px] uppercase font-bold">Node Operator</span>
                                  <span className="font-extrabold">{spatialResult.node_id}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500 block text-[7px] uppercase font-bold">Gross Invoice</span>
                                  <span className="font-extrabold text-[#00ff88]">£{(spatialResult.billing?.gross_pence / 100).toFixed(2)}</span>
                                </div>
                                <div className="col-span-full md:col-span-1">
                                  <span className="text-slate-500 block text-[7px] uppercase font-bold">Net SecurePayment Settlement</span>
                                  <span className="font-extrabold text-cyan-400">£{(spatialResult.billing?.net_SecurePayment_settlement / 100).toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500 block text-[7px] uppercase font-bold">NexoraX 1% Levy</span>
                                  <span className="font-mono text-amber-400">£{(spatialResult.billing?.levy_pence / 100).toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500 block text-[7px] uppercase font-bold">20% 10% Maintenance & Hosting Fee Tax</span>
                                  <span className="font-mono text-rose-400">£{(spatialResult.billing?.gavel_fee_pence / 100).toFixed(2)}</span>
                                </div>
                                <div className="col-span-full border-t border-slate-900/50 pt-1 flex flex-col gap-0.5">
                                  <span className="text-slate-500 text-[7px] uppercase font-bold">Clearing Route Target</span>
                                  <span className="text-slate-300 text-[8px]">{spatialResult.NexoraX_metrics?.SecurePayment_bank_route}</span>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-1.5 text-[8px] text-cyan-400 uppercase font-bold">
                                <Check className="w-3.5 h-3.5 text-emerald-400" />
                                Coordinates routed safely into Spatial WebGL matrix on NexoraX Digital
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* NEW SYSTEM LOGS DISPLAY MATRIX */}
                    <div className="space-y-2.5">
                      <div className="flex justify-between items-center text-[8.5px] text-zinc-500 font-bold uppercase tracking-widest">
                        <span>HOLOGRAPHIC DIAGNOSTICS & SYSTEM LOGS</span>
                        <span className="text-cyan-400 font-mono animate-pulse">● LIVE CONSOLE FEED</span>
                      </div>
                      
                      <div className="bg-slate-950/85 p-4 rounded-xl border border-slate-900 font-mono text-[9px] text-slate-400 space-y-1.5 max-h-[160px] overflow-y-auto">
                        {simulatedLogs.map((log, index) => (
                          <div key={index} className="flex flex-col sm:flex-row gap-1 sm:gap-2 border-b border-slate-900/40 pb-1 last:border-0 last:pb-0 text-left">
                            <span className="text-slate-600 font-bold">[{log.time}]</span>
                            <div className="flex items-center gap-1.5 min-w-[130px]">
                              <span className={`${
                                log.status === 'SUCCESS' ? 'text-emerald-400' :
                                log.status === 'ONLINE' ? 'text-cyan-400' :
                                log.status === 'SECURE' ? 'text-[#4f8eff]' :
                                log.status === 'RESOLVED' ? 'text-emerald-400 font-black' :
                                'text-amber-400'
                              } font-extrabold`}>
                                {log.info}
                              </span>
                              <span className="text-[7.5px] px-1 py-0.05 bg-slate-900 text-slate-500 rounded border border-slate-800/80">{log.status}</span>
                            </div>
                            <span className="text-slate-400/90 whitespace-normal break-all sm:break-normal">{log.detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Footer Controls / Operational actions */}
                    <div className="flex flex-wrap justify-between items-center gap-4 border-t border-slate-900 pt-5 mt-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => toggleTaskCompletion(detailedTask.id)}
                          className={`px-4 py-2 text-[10px] font-black uppercase rounded-lg border transition-all duration-300 cursor-pointer ${
                            detailedTask.completed 
                              ? 'bg-[#00ff88]/15 border-[#00ff88]/50 text-[#00ff88]' 
                              : 'bg-black/60 border-slate-800 text-emerald-400 hover:bg-[#00ff88] hover:text-[#050a18] hover:border-[#00ff88]'
                          }`}
                        >
                          {detailedTask.completed ? '✓ SOLVED PROTOCOL' : '⚙ COORDINATE SOLUTION'}
                        </button>
                        <button
                          onClick={() => {
                            removeTaskNode(detailedTask.id);
                            setDetailedTaskId(null);
                          }}
                          className="px-4 py-2 text-[10px] font-black uppercase rounded-lg border bg-black/60 border-slate-800 text-rose-400 hover:bg-rose-500 hover:text-[#050a18] hover:border-rose-500 transition-all duration-300 cursor-pointer"
                        >
                          LIQUIDATE NODE
                        </button>
                      </div>
                      <button
                        onClick={() => setDetailedTaskId(null)}
                        className="px-4 py-2 text-[10px] font-black bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 rounded-lg text-slate-300 uppercase tracking-widest transition-all duration-300 cursor-pointer"
                      >
                        CLOSE OVERLAY
                      </button>
                    </div>

                  </motion.div>
                </div>
              );
            })()}
          </AnimatePresence>

        </main>

      </div>

      {/* Footer corporate compliance credentials */}
      <div className="p-4 bg-slate-950/95 border-t border-slate-900 text-[9px] text-slate-500 uppercase tracking-widest font-bold flex flex-col sm:flex-row justify-between items-center gap-3 text-center">
        <span>NEXORAX Resource resource DEPLOYMENT MATRIX OK</span>
        <span>MAPPING Secure Payment Provider (Verified Payment Configuration // Secure Billing Provider) NOMINAL STATE</span>
        <span>CloudSync-02 MIRROR CLOUD BACKUP VERIFIED</span>
      </div>

    </section>
  );
}
